import React from "react"

import { css } from "@emotion/react"
import { type ReactConfirmAlertProps, confirmAlert } from "react-confirm-alert"

import type { IEnableConfirmationAdditionalOptions } from "@front-shared/types/objects.types"

import { NIRVANA_THEME } from "../config/theme.config"
import { ButtonElement } from "../elements/button.element"
import { PopupWrapperElement } from "../elements/popupWrapper.element"
import { PrimaryText } from "../elements/primaryText.element"

/**
 * Class for working confirmation dialogs
 */
export class ConfirmationHelper {
    /**
     * Confirm some action in our application
     */
    static enableConfirmation(
        onConfirm: () => void,
        title = "Are you sure?",
        content?: React.ReactNode | React.ReactNode[],
        onCancel?: () => void,
        confirmLabel = "Confirm",
        cancelLabel = "Cancel",
        withCancelBtn = true,
        additionalOptions?: IEnableConfirmationAdditionalOptions,
    ) {
        const options: ReactConfirmAlertProps = {
            customUI: ({ onClose }) => {
                const combinedCancel = () => {
                    onClose()
                    onCancel?.()
                }

                const combinedConfirm = () => {
                    onClose()
                    onConfirm()
                }

                return (
                    <PopupWrapperElement
                        isActive={true}
                        onDone={onClose}
                        popupCss={css({
                            width: additionalOptions?.width || 440,
                        })}
                        closeOnOutsideClick={
                            additionalOptions?.isNested
                                ? false
                                : !additionalOptions?.ignoreOnOutsideClick
                        }
                        withCloseIcon={additionalOptions?.withCloseIcon}
                        increasedZIndex={additionalOptions?.isNested}
                        overlayZIndex={
                            additionalOptions?.isNested
                                ? NIRVANA_THEME.popupIncreasedZIndex - 1
                                : undefined
                        }
                    >
                        <PrimaryText
                            centered={!additionalOptions?.isLeftAligned}
                            typography="h4"
                        >
                            {title}
                        </PrimaryText>

                        {content
                            ? (Array.isArray(content)
                                  ? content
                                  : [content]
                              ).map((contentPart, index) => {
                                  return (
                                      <PrimaryText
                                          key={index}
                                          centered={
                                              !additionalOptions?.isLeftAligned
                                          }
                                          typography="text"
                                          css={css({
                                              marginTop: 32,
                                          })}
                                      >
                                          {contentPart}
                                      </PrimaryText>
                                  )
                              })
                            : null}

                        <div className="flex items-center justify-center mt-[32px]">
                            {withCancelBtn && (
                                <div className="flex-1 mr-[16px]">
                                    <ButtonElement
                                        buttonClassName="w-full"
                                        label={cancelLabel}
                                        type="default"
                                        htmlType="button"
                                        size={
                                            additionalOptions?.buttonSize ||
                                            "large"
                                        }
                                        onClick={combinedCancel}
                                    />
                                </div>
                            )}

                            {!additionalOptions?.hideConfirmButton && (
                                <div className="flex-1">
                                    <ButtonElement
                                        buttonClassName="w-full"
                                        label={confirmLabel}
                                        type="primary"
                                        size={
                                            additionalOptions?.buttonSize ||
                                            "large"
                                        }
                                        htmlType="button"
                                        onClick={combinedConfirm}
                                    />
                                </div>
                            )}
                        </div>
                    </PopupWrapperElement>
                )
            },
        }

        confirmAlert(options)
    }
}
